import { useState, useEffect } from 'react';

interface UseColumnDragProps {
  defaultColumnOrder: string[];
  storageKey: string;
}

export const useColumnDrag = ({ defaultColumnOrder, storageKey }: UseColumnDragProps) => {
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(storageKey);
    if (!saved) {
      localStorage.setItem(storageKey, JSON.stringify(defaultColumnOrder));
      return defaultColumnOrder;
    }

    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
    }

    localStorage.setItem(storageKey, JSON.stringify(defaultColumnOrder));
    return defaultColumnOrder;
  });

  const [draggedColumn, setDraggedColumn] = useState<string>('');

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (!saved) {
      setColumnOrder(defaultColumnOrder);
      localStorage.setItem(storageKey, JSON.stringify(defaultColumnOrder));
      return;
    }

    try {
      const savedOrder = JSON.parse(saved);
      if (!Array.isArray(savedOrder)) {
        throw new Error('column order corrupted');
      }

      const hasAllColumns = defaultColumnOrder.every(col => savedOrder.includes(col));
      if (!hasAllColumns) {
        throw new Error('column order missing entries');
      }

      setColumnOrder(savedOrder);
    } catch (_) {
      setColumnOrder(defaultColumnOrder);
      localStorage.setItem(storageKey, JSON.stringify(defaultColumnOrder));
    }
  }, [defaultColumnOrder, storageKey]);

  const handleDragStart = (e: React.DragEvent, columnKey: string) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetColumnKey: string) => {
    e.preventDefault();
    
    if (!draggedColumn || draggedColumn === targetColumnKey) {
      return;
    }

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnKey);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    setColumnOrder(newOrder);
    localStorage.setItem(storageKey, JSON.stringify(newOrder));
    setDraggedColumn('');
  };

  const handleDragEnd = () => {
    setDraggedColumn('');
  };

  const resetColumnOrder = () => {
    setColumnOrder(defaultColumnOrder);
    localStorage.setItem(storageKey, JSON.stringify(defaultColumnOrder));
  };

  return {
    columnOrder,
    draggedColumn,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    resetColumnOrder
  };
}; 